import React, { useEffect, useState, useContext } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { applyFilterMutationWithError } from 'gqlQueries/filterQueries';
import { FilterContextData } from 'contexts/FilterContext';
import { useLocation } from 'react-router-dom';
import { UserContext } from 'contexts/ConfigContext';
import { deleteTypeNameFromResponse, getConfig, getScreenFilterDetails } from 'utils/helperMethods';

const errorMessage: string = 'Something went wrong!';

interface Props {
    onComplete: Function;
    onError?: Function;
}

function useFetchApplyFilterWithError({ onComplete, onError }: Props) {
    let route = useLocation();
    const { appConfig } = useContext(UserContext);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    let { setFilterNullError, filterNullError } = useContext(FilterContextData);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    const { screenName, screenFilterConfig, currentScreenFeatureName } = getScreenFilterDetails(
        route,
        globalFilterConfig,
    );

    const [mutateApplyFilter, { data: applyFilterData, loading: applyFilterLoading, error: applyFilterError }] =
        useMutation(applyFilterMutationWithError, {
            onCompleted: onFetchComplete,
            onError: onFetchError,
        });
    useEffect(() => {
        setLoading(applyFilterLoading);
    }, [applyFilterLoading]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        setFilterNullError('');
        deleteTypeNameFromResponse(payload);
        try {
            const tempData = onComplete(payload);
            if (tempData) setData(tempData);
        } catch (error) {
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        //sending empty data to error to avoid null data being sent
        if (onError) onError();
        setData([]);
        setLoading(false);
        setError(error);
    }

    return {
        loading,
        error,
        data,
        applyFilterMutate: (variables: any) => {
            const tempVariables = { ...variables };
            tempVariables.variables.featureName = currentScreenFeatureName;
            setError(null);
            mutateApplyFilter(variables);
        },
    };
}

export { useFetchApplyFilterWithError };
